import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogContent,
  Snackbar,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../auth/AuthContext';
import { createContextAwareApiClient } from '../api/contextAwareApiClient';
import { energyService } from '../api/energyApi';
import InlineEditForm from './InlineEditForm';

/**
 * FleetSelector component provides a dropdown for selecting a fleet
 * based on the current customer context.
 */
function FleetSelector() {
  const auth = useAuth();
  const { 
    customerContext, 
    fleetContext, 
    setFleet, 
    clearFleet,
    getContextData,
    validateContext 
  } = useAppContext();
  const customerId = customerContext?.customerId;
  
  // Use ref to store the service to prevent infinite loops
  const fleetServiceRef = useRef();
  fleetServiceRef.current = createContextAwareApiClient(getContextData, validateContext).fleetService;
  
  const [fleets, setFleets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const requestVersion = useRef(0);
  const invalidateRequests = useCallback(() => { ++requestVersion.current; }, []);
  const selectedFleetRef = useRef(fleetContext);
  selectedFleetRef.current = fleetContext;
  const contextRef = useRef({ setFleet, clearFleet });
  contextRef.current = { setFleet, clearFleet };
  const authRef = useRef(auth);
  authRef.current = auth;

  const fleetFields = [
    {
      name: 'fleetId',
      label: 'Fleet ID',
      required: true,
      helperText: 'Enter a unique fleet identifier'
    },
    {
      name: 'fleetName',
      label: 'Fleet Name',
      required: true,
      helperText: 'Enter the fleet name'
    },
    {
      name: 'fleetLocation',
      label: 'Location',
      required: false
    },
    {
      name: 'fleetLatitude',
      label: 'Latitude',
      type: 'number',
      required: false,
      helperText: 'Enter latitude in decimal degrees'
    },
    {
      name: 'fleetLongitude',
      label: 'Longitude',
      type: 'number',
      required: false,
      helperText: 'Enter longitude in decimal degrees'
    },
    {
      name: 'fleetDescription',
      label: 'Description',
      multiline: true,
      rows: 3,
      required: false
    }
  ];

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchFleets = useCallback(async (customerId, retryAttempt = 0) => {
    if (!customerId) {
      console.log('🚫 FLEET SELECTOR: No customer ID provided');
      return;
    }

    console.log(`🔄 FLEET SELECTOR: Starting fleet fetch (attempt ${retryAttempt + 1})`);
    console.log('📋 Customer ID:', customerId);
    
    const request = ++requestVersion.current;
    setLoading(true);
    setError(null);
    
    try {
      console.log('🌐 FLEET SELECTOR: Calling API for customer:', customerId);
      const response = await energyService.getFleets(authRef.current.isSuperuser ? { customerId } : {});
      if (request !== requestVersion.current) return [];
      console.log('📡 FLEET SELECTOR: API response received:', response);
      
      if (response.success && response.data) {
        // Handle the API response structure correctly
        // After transformation, the structure is: { status: "success", data: [...], metadata: {...} }
        const fleetData = response.data.data || [];
        console.log('📊 FLEET SELECTOR: Fleet data extracted:', fleetData);
        console.log('🔢 FLEET SELECTOR: Number of fleets:', fleetData.length);
        
        if (fleetData.length > 0) {
          console.log('✅ FLEET SELECTOR: Fleets found, updating state');
          fleetData.forEach((fleet, index) => {
            console.log(`   Fleet ${index + 1}: ${fleet.fleetId} - ${fleet.fleetDescription}`);
          });
        } else {
          console.log('⚠️ FLEET SELECTOR: No fleets found for customer');
        }
        
        setFleets(fleetData);
        const previousId = selectedFleetRef.current?.fleetId;
        const selected = fleetData.length === 1 ? fleetData[0] : fleetData.find((fleet) => fleet.fleetId === previousId);
        if (selected) contextRef.current.setFleet(selected);
        else if (previousId) contextRef.current.clearFleet();
        setRetryCount(0); // Reset retry count on success
        return fleetData;
      } else {
        console.error('❌ FLEET SELECTOR: Failed to load fleets:', response);
        setError(`Failed to load fleets: ${response.message || 'Unknown error'}`);
        setFleets([]);
        contextRef.current.clearFleet();
        return [];
      }
    } catch (err) {
      if (request !== requestVersion.current) return [];
      console.error('💥 FLEET SELECTOR: Error fetching fleets:', err);
      
      // Retry logic for network errors
      if (err.message === 'Network Error' && retryAttempt < 2) {
        console.log(`🔄 FLEET SELECTOR: Network error, retrying... (${retryAttempt + 1}/3)`);
        setRetryCount(retryAttempt + 1);
        
        // Wait 1 second before retrying
        setTimeout(() => {
          if (request === requestVersion.current) fetchFleets(customerId, retryAttempt + 1);
        }, 1000);
        return;
      }
      
      setError(`Error: ${err.message}`);
      setFleets([]);
      contextRef.current.clearFleet();
      return [];
    } finally {
      if (request === requestVersion.current) setLoading(false);
      console.log('🏁 FLEET SELECTOR: Fleet fetch completed');
    }
  }, []); // Remove fleetService dependency to prevent infinite loop

  // Fetch fleets when the customer context changes
  useEffect(() => {
    invalidateRequests();
    setFleets([]);
    if (!customerId) {
      console.log('🚫 FLEET SELECTOR: No customer context, clearing fleets');
      setFleets([]);
      clearFleet();
      setError(null);
      setRetryCount(0);
      return;
    }
    
    // Only fetch if we have a valid customer ID
    fetchFleets(customerId);
    return invalidateRequests;
  }, [customerId, fetchFleets, clearFleet, invalidateRequests]);

  const handleChange = (event) => {
    const fleetId = event.target.value;
    console.log('Fleet selection changed to:', fleetId);
    
    if (fleetId) {
      const selectedFleet = fleets.find(f => f.fleetId === fleetId);
      console.log('Selected fleet:', selectedFleet);
      setFleet(selectedFleet);
    } else {
      clearFleet();
    }
  };

  const handleRetry = () => {
    if (customerContext?.customerId) {
      fetchFleets(customerContext.customerId);
    }
  };

  const handleAddFleet = () => {
    if (!auth.isSuperuser || !customerContext?.customerId) return;
    setCreateOpen(true);
  };

  const handleCreateFleet = async (formData) => {
    if (!auth.isSuperuser || !customerContext?.customerId) return;

    setIsSubmitting(true);
    try {
      const response = await fleetServiceRef.current.createFleet({
        ...formData,
        customerId: customerContext.customerId
      });

      if (!response.success) {
        setNotification({
          open: true,
          message: response.message || 'Failed to create fleet',
          severity: 'error'
        });
        return;
      }

      const refreshedFleets = await fetchFleets(customerContext.customerId);
      const createdFleet = Array.isArray(refreshedFleets)
        ? refreshedFleets.find((fleet) => fleet.fleetId === formData.fleetId)
        : null;
      if (createdFleet) setFleet(createdFleet);

      setCreateOpen(false);
      setNotification({
        open: true,
        message: 'Fleet created successfully',
        severity: 'success'
      });
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'Failed to create fleet',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" component="div">
          Fleet
        </Typography>
        {auth.isSuperuser && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            disabled={!customerContext}
            onClick={handleAddFleet}
          >
            New Fleet
          </Button>
        )}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {!loading && fleets.length === 1 ? (
          <Typography variant="body1" fontWeight={600}>{fleets[0].fleetName || fleets[0].fleetDescription || fleets[0].fleetId}</Typography>
        ) : <FormControl fullWidth>
          <InputLabel id="fleet-selector-label">Fleet</InputLabel>
          <Select
            labelId="fleet-selector-label"
            value={fleets.some((fleet) => fleet.fleetId === fleetContext?.fleetId) ? fleetContext.fleetId : ''}
            label="Fleet"
            onChange={handleChange}
            disabled={loading || !customerContext || fleets.length === 0}
          >
            {loading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
                <Typography sx={{ ml: 1 }}>
                  Loading Fleets...{retryCount > 0 ? ` (Retry ${retryCount})` : ''}
                </Typography>
              </MenuItem>
            ) : !customerContext ? (
              <MenuItem value="" disabled>Select a customer first</MenuItem>
            ) : error ? (
              <MenuItem value="" disabled>Error loading fleets</MenuItem>
            ) : fleets.length === 0 ? (
              <MenuItem value="" disabled>No fleets found</MenuItem>
            ) : (
              fleets.map((fleet) => (
                <MenuItem key={fleet.fleetId} value={fleet.fleetId}>
                  {fleet.fleetName || fleet.fleetDescription || fleet.fleetId}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>}
      </Stack>
      {error && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Typography color="error" variant="caption">
            {error}
          </Typography>
          <Button size="small" onClick={handleRetry} disabled={loading}>
            Retry
          </Button>
        </Stack>
      )}
      <Dialog
        open={createOpen}
        onClose={() => !isSubmitting && setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="create-fleet-dialog-title"
        PaperProps={{ sx: { maxHeight: 'calc(100dvh - 32px)', overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <InlineEditForm
            data={{
              fleetId: '',
              fleetName: '',
              fleetLocation: '',
              fleetLatitude: '',
              fleetLongitude: '',
              fleetDescription: ''
            }}
            fields={fleetFields}
            onSave={handleCreateFleet}
            onCancel={() => setCreateOpen(false)}
            isSubmitting={isSubmitting}
            title="Create New Fleet"
            titleId="create-fleet-dialog-title"
            dialogLayout
            saveLabel="Create Fleet"
            showFinished={false}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={notification.severity}
          variant="filled"
          onClose={() => setNotification((current) => ({ ...current, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FleetSelector;
